class ProductAccordionAnimator {
  constructor(details) {
    this.details = details;
    this.summary = details.querySelector(':scope > summary');
    this.content = this.summary?.nextElementSibling;
    this.animation = null;
    this.contentAnimation = null;
    this.isClosing = false;
    this.isExpanding = false;
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!this.summary || !this.content) return;

    this.details.dataset.smoothAccordion = 'true';
    this.summary.addEventListener('click', this.onClick.bind(this));
  }

  onClick(event) {
    event.preventDefault();

    if (this.reduceMotion) {
      this.details.open = !this.details.open;
      this.summary.setAttribute('aria-expanded', this.details.open);
      return;
    }

    if (this.isClosing || !this.details.open) {
      this.open();
    } else if (this.isExpanding || this.details.open) {
      this.close();
    }
  }

  open() {
    const startHeight = `${this.details.offsetHeight}px`;

    this.cancelAnimations();
    this.details.style.height = startHeight;
    this.details.open = true;
    this.summary.setAttribute('aria-expanded', 'true');
    this.details.classList.add('is-animating', 'is-expanding');

    const endHeight = `${this.getNaturalHeight()}px`;
    this.details.style.height = startHeight;
    this.isExpanding = true;

    this.animation = this.details.animate(
      { height: [startHeight, endHeight] },
      { duration: 360, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
    );
    this.contentAnimation = this.content.animate(
      [
        { opacity: 0, transform: 'translateY(-0.6rem)' },
        { opacity: 1, transform: 'translateY(0)' },
      ],
      { duration: 300, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
    );

    this.animation.onfinish = () => this.finish(true);
  }

  close() {
    const startHeight = `${this.details.offsetHeight}px`;
    const endHeight = `${this.summary.offsetHeight}px`;

    this.cancelAnimations();
    this.details.style.height = startHeight;
    this.details.classList.add('is-animating', 'is-closing');
    this.summary.setAttribute('aria-expanded', 'false');
    this.isClosing = true;

    this.animation = this.details.animate(
      { height: [startHeight, endHeight] },
      { duration: 320, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' }
    );
    this.contentAnimation = this.content.animate(
      [
        { opacity: 1, transform: 'translateY(0)' },
        { opacity: 0, transform: 'translateY(-0.4rem)' },
      ],
      { duration: 220, easing: 'ease-out' }
    );

    this.animation.onfinish = () => this.finish(false);
  }

  getNaturalHeight() {
    const currentHeight = this.details.style.height;
    this.details.style.height = 'auto';
    const naturalHeight = this.details.offsetHeight;
    this.details.style.height = currentHeight;
    return naturalHeight;
  }

  cancelAnimations() {
    this.animation?.cancel();
    this.contentAnimation?.cancel();
    this.animation = null;
    this.contentAnimation = null;
    this.isClosing = false;
    this.isExpanding = false;
    this.details.classList.remove('is-closing', 'is-expanding');
  }

  finish(isOpen) {
    this.details.open = isOpen;
    this.details.style.height = '';
    this.details.classList.remove('is-animating', 'is-closing', 'is-expanding');
    this.summary.setAttribute('aria-expanded', isOpen);
    this.animation = null;
    this.contentAnimation = null;
    this.isClosing = false;
    this.isExpanding = false;
  }
}

function initializeProductAccordions(root = document) {
  const accordions = root.matches?.('.product__accordion details:not([data-smooth-accordion])')
    ? [root]
    : root.querySelectorAll?.('.product__accordion details:not([data-smooth-accordion])') || [];

  accordions.forEach((details) => {
    new ProductAccordionAnimator(details);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeProductAccordions());
} else {
  initializeProductAccordions();
}

document.addEventListener('shopify:section:load', (event) => initializeProductAccordions(event.target));

const productAccordionObserver = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) initializeProductAccordions(node);
    });
  });
});

productAccordionObserver.observe(document.body, { childList: true, subtree: true });
